import { useState, useEffect } from 'react'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/common/ui/button'
import { SidebarNav } from '@/components/common/layout/sidebar/sidebar-nav'
import { useSidebarStore } from '@/stores/sidebar-store'

interface SidebarProps {
  className?: string
}

export function Sidebar({ className }: SidebarProps) {
  const { isCollapsed, setIsCollapsed, isMobile, setIsMobile } = useSidebarStore()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (mobile) {
        setIsCollapsed(true)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [setIsMobile, setIsCollapsed])

  const toggleSidebar = () => {
    if (isMobile) {
      setIsOpen(!isOpen)
    } else {
      setIsCollapsed(!isCollapsed)
    }
  }

  if (isMobile) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          className="fixed top-4 left-4 z-50 md:hidden"
          onClick={toggleSidebar}
        >
          <Menu className="h-5 w-5" />
        </Button>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />
            <div
              data-testid="sidebar"
              className="fixed left-0 top-0 h-full w-[260px] bg-background border-r z-50 md:hidden"
            >
              <SidebarNav onNavigate={() => setIsOpen(false)} />
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <div
      data-testid="sidebar"
      className={cn(
        'relative h-full border-r transition-all duration-300 bg-white/95 backdrop-blur-sm',
        isCollapsed
          ? 'w-[60px] shadow-[0_10px_35px_-20px_rgba(79,70,229,0.55)] border-indigo-50'
          : 'w-[260px]',
        className
      )}
    >
      <SidebarNav onToggleSidebar={toggleSidebar} />
    </div>
  )
}
